import { useTranslation } from 'react-i18next';
import { Reveal } from '../motion/Reveal';
import globe from '../assets/figma/images/globe-vector.svg';
import card from '../assets/figma/images/coverage-card.webp';
import wordmark from '../assets/figma/icons/wordmark-w.svg';

/**
 * Figma 59:8594 — heading over a dark card (#0b1219, r56, clipped) whose globe
 * hangs off the top-right corner.
 *
 * The globe and the card in front of it were one flattened image; they are
 * separated here so each can move on its own. The globe is the file's own
 * vector — the raster export arrives already cropped to the card, which would
 * have swung its cut edges into view as it turned.
 *
 * Its placement can't be read off the frame box, which is padding around the
 * drawing. It was solved from the cropped render instead: the circle's centre
 * sits at (978.25, 219) of the card with a radius of 240.25, which the chord at
 * the card's top edge confirms to the pixel. The vector's own centre is
 * (282.176, 282.176), so it is hung by that — overflowing the top edge exactly
 * as the design does. It turns slowly and forever; the card drifts against it,
 * and that difference is what reads as depth.
 */
export function Coverage() {
  const { t } = useTranslation();

  return (
    <section id="coverage" className="mx-auto w-full px-[16px] lg:w-[1208px] lg:px-0">
      <Reveal>
        <h2 className="text-center font-display text-[32px] font-medium leading-[1.05] text-ink sm:text-[44px] lg:text-[72px] lg:leading-[0.9]">
          {t('coverage.title1')}
          <br />
          {t('coverage.title2')}
        </h2>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="relative mt-[32px] w-full overflow-hidden rounded-[32px] bg-ink lg:mt-[63px] lg:h-[439px] lg:w-[1208px] lg:rounded-[56px]">
          <img
            src={globe}
            alt=""
            className="aw-spin pointer-events-none absolute -right-[22%] -top-[18%] size-[70%] max-w-none opacity-40 lg:left-[696.07px] lg:right-auto lg:top-[-63.18px] lg:size-[564.35px] lg:opacity-100"
          />

          <img
            src={card}
            alt=""
            className="aw-drift-tilt pointer-events-none absolute right-[10px] top-[14px] h-auto w-[30%] max-w-none lg:left-[799.12px] lg:right-auto lg:top-[77.4px] lg:h-[279px] lg:w-[373px]"
          />

          <div className="relative flex flex-col gap-[24px] p-[24px] lg:absolute lg:left-[32px] lg:top-[32px] lg:h-[375px] lg:w-[660px] lg:gap-0 lg:p-0 lg:justify-between">
            <div className="flex flex-col pr-[34%] lg:pr-0">
              <p className="text-[24px] font-light leading-none text-white sm:text-[34px] lg:mb-[-4px] lg:text-[64px]">
                {t('coverage.line1')}
              </p>
              <div className="flex items-center gap-[8px] lg:mb-[-4px] lg:gap-[19px]">
                <img src={wordmark} alt="" className="h-[19px] w-[36px] sm:h-[27px] sm:w-[50px] lg:h-[52px] lg:w-[97px]" />
                <p className="text-[24px] font-light leading-none text-white sm:text-[34px] lg:text-[64px]">
                  {t('coverage.line2')}
                </p>
              </div>
              <p className="text-[24px] font-medium leading-none text-white sm:text-[34px] lg:text-[64px]">
                {t('coverage.line3')}
              </p>
            </div>

            <p className="text-[14px] font-normal leading-[1.4] text-white sm:text-[16px] lg:text-[24px] lg:leading-[1.16]">
              {t('coverage.bodyLead')}
              <span className="text-white/55">{t('coverage.bodyRest')}</span>
            </p>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
